import {
    AutoComplete,
    Button,
    DatePicker,
    Input,
    InputNumber,
    Modal,
    Select,
    Table,
    Tabs,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import calculateKarat, { CalculationResult, KaratRow } from '../utils/karatCalculations';

interface AddRowButtonProps {
    onClick: () => void;
    t: (key: string) => string;
}

const AddRowButton: React.FC<AddRowButtonProps> = ({ onClick, t }) => (
    <Button type="dashed" onClick={onClick} style={{ width: '100%' }}>
        + {t('metals.addRow')}
    </Button>
);

interface TableFooterProps {
    onAddRow: () => void;
    t: (key: string) => string;
    rowsEditable: boolean;
}

const TableFooter: React.FC<TableFooterProps> = ({ onAddRow, t, rowsEditable }) =>
    rowsEditable ? <AddRowButton onClick={onAddRow} t={t} /> : null;

export interface KaratCalculatorProps {
    showControls?: boolean;
    showLoadSelect?: boolean;
    /** Optional rows to load when component mounts or updates */
    initialRows?: RowData[];
    /** Optional initial after mass */
    initialAfterMass?: number;
    /** Optional initial after karat */
    initialAfterKarat?: number;
    /** Whether to show the delete column. Default is false (hidden) */
    showDeleteColumn?: boolean;
    /** Whether rows are editable */
    rowsEditable?: boolean;
    /** Column names for customization */
    columnNames?: {
        qty?: string;
        quantityToMelt?: string;
    };
    /** Callback to get melting data */
    onMeltingDataChange?: (meltingData: RowData[]) => void;
    /** Callback when afterMass or afterKarat changes */
    onAfterValuesChange?: (afterMass?: number, afterKarat?: number) => void;
    /** Callback when losses data changes */
    onLossesChange?: (losses: {
        lossGr: number;
        lossPercent: number;
        totalLossPercent: number;
    }) => void;
}

export type MetalType = 'Gold' | 'Silver' | 'Platinum' | 'Palladium';

export interface RowData extends KaratRow {
    metalType?: MetalType;
    quantityToMelt?: number;
    pmp?: number;
}

const finenessOptions = [375, 585, 750, 800, 900, 916, 958, 999, 999.9];
const metalTypeOptions: MetalType[] = ['Gold', 'Silver', 'Platinum', 'Palladium'];

const KaratCalculator = ({
    showControls = false,
    showLoadSelect = false,
    initialRows,
    initialAfterMass,
    initialAfterKarat,
    showDeleteColumn = false,
    rowsEditable = true,
    columnNames = {},
    onMeltingDataChange,
    onAfterValuesChange,
    onLossesChange,
}: KaratCalculatorProps) => {
    const { t } = useTranslation();
    const [modal, modalContextHolder] = Modal.useModal();
    type MetalState = {
        rows: RowData[];
        afterMass?: number;
        afterKarat?: number;
        targetKarat?: number;
        addKarat?: number;
    };

    const createDefaultState = React.useCallback(
        (metal: MetalType): MetalState => ({
            rows: [{ metalType: metal, karat: undefined, grams: undefined, pmp: undefined }],
        }),
        [],
    );

    const [activeMetal, setActiveMetal] = useState<MetalType>('Gold');
    const [metalStates, setMetalStates] = useState<Record<MetalType, MetalState>>({
        Gold: createDefaultState('Gold'),
        Silver: createDefaultState('Silver'),
        Platinum: createDefaultState('Platinum'),
        Palladium: createDefaultState('Palladium'),
    });
    const [name, setName] = useState('');
    const [date, setDate] = useState<Dayjs | null>(dayjs());

    useEffect(() => {
        if (initialRows?.length) {
            const updated: Record<MetalType, MetalState> = {
                Gold: { rows: [] },
                Silver: { rows: [] },
                Platinum: { rows: [] },
                Palladium: { rows: [] },
            };
            initialRows.forEach(r => {
                const metal = r.metalType ?? 'Gold';
                updated[metal].rows.push({ ...r, metalType: metal });
            });
            Object.keys(updated).forEach(m => {
                if (!updated[m as MetalType].rows.length) {
                    updated[m as MetalType] = createDefaultState(m as MetalType);
                }
            });
            setMetalStates(updated);
        }
    }, [initialRows, createDefaultState]);

    useEffect(() => {
        if (initialAfterMass != null || initialAfterKarat != null) {
            setMetalStates(prev => {
                const updated = { ...prev };
                (Object.keys(updated) as MetalType[]).forEach(m => {
                    updated[m] = {
                        ...updated[m],
                        afterMass: initialAfterMass ?? updated[m].afterMass,
                        afterKarat: initialAfterKarat ?? updated[m].afterKarat,
                    };
                });
                return updated;
            });
        }
    }, [initialAfterMass, initialAfterKarat]);

    // Notify parent about melting data changes
    useEffect(() => {
        if (onMeltingDataChange) {
            const allRows = Object.values(metalStates).flatMap(state => state.rows);
            const meltingData = allRows.filter(row => row.quantityToMelt && row.quantityToMelt > 0);
            onMeltingDataChange(meltingData);
        }
    }, [metalStates, onMeltingDataChange]);

    const currentState = metalStates[activeMetal];
    const setCurrentState = (state: MetalState) => {
        setMetalStates(prev => ({ ...prev, [activeMetal]: state }));
    };

    const addRow = () => {
        setCurrentState({
            ...currentState,
            rows: [
                ...currentState.rows,
                { metalType: activeMetal, karat: undefined, grams: undefined, pmp: undefined },
            ],
        });
    };

    type RowDataFieldValue = {
        metalType: MetalType;
        karat?: number;
        grams?: number;
        adjust?: number;
        quantityToMelt?: number;
        pmp?: number;
    };

    const handleRowChange = <K extends keyof RowDataFieldValue>(
        index: number,
        field: K,
        value: RowDataFieldValue[K],
    ) => {
        const newRows = [...currentState.rows];
        const row = newRows[index];
        if (row) {
            row[field] = value;
        }
        setCurrentState({ ...currentState, rows: newRows });
    };

    const confirmDeleteRow = (index: number) => {
        if (currentState.rows.length <= 1) {
            return; // Don't allow deleting the last row
        }

        const handleDelete = () => {
            setCurrentState({
                ...currentState,
                rows: currentState.rows.filter((_, i) => i !== index),
            });
        };

        modal.confirm({
            title: t('metals.confirmDelete'),
            content: t('metals.confirmDeleteRow'),
            onOk: handleDelete,
        });
    };

    const {
        totalMass,
        total24,
        avgKarat,
        dupaTopitContinut,
        pierdereGr,
        pierdereProc,
        pierdereProcTotal,
        gramsToAdd,
        finalMass,
        finalPure,
    }: CalculationResult = calculateKarat({
        rows: currentState.rows,
        afterMass: currentState.afterMass,
        afterKarat: currentState.afterKarat,
        targetKarat: currentState.targetKarat,
        addKarat: currentState.addKarat,
    });

    // Calculate melting totals
    const meltingTotalMass = currentState.rows.reduce(
        (total, row) => total + (row.quantityToMelt ?? 0),
        0,
    );
    const meltingTotal24 = currentState.rows.reduce(
        (total, row) => total + ((row.quantityToMelt ?? 0) * (row.karat ?? 0)) / 999.99,
        0,
    );
    const meltingAvgKarat = meltingTotalMass > 0 ? (meltingTotal24 * 999.99) / meltingTotalMass : 0;

    // Calculate PMP (Weighted Average Price) totals
    const totalPmpValue = currentState.rows.reduce(
        (total, row) => total + (row.grams ?? 0) * (row.pmp ?? 0),
        0,
    );
    const avgPmp = totalMass > 0 ? totalPmpValue / totalMass : 0;

    const meltingTotalPmpValue = currentState.rows.reduce(
        (total, row) => total + (row.quantityToMelt ?? 0) * (row.pmp ?? 0),
        0,
    );
    const meltingAvgPmp = meltingTotalMass > 0 ? meltingTotalPmpValue / meltingTotalMass : 0;

    // Notify parent about losses changes when afterMass and afterKarat are available
    useEffect(() => {
        if (onLossesChange && currentState.afterMass != null && currentState.afterKarat != null) {
            onLossesChange({
                lossGr: pierdereGr,
                lossPercent: pierdereProc,
                totalLossPercent: pierdereProcTotal,
            });
        }
    }, [
        onLossesChange,
        pierdereGr,
        pierdereProc,
        pierdereProcTotal,
        currentState.afterMass,
        currentState.afterKarat,
    ]);

    const columns = [
        {
            title: '#',
            render: (_: unknown, __: unknown, index: number) => index + 1,
        },
        {
            title: t('metals.karat'),

            dataIndex: 'karat',
            render: (_: unknown, row: RowData, index: number) => (
                <AutoComplete
                    value={row.karat?.toString() ?? ''}
                    className="karat-input-autocomplete"
                    style={{
                        width: 80,
                        fontSize: '11px',
                        height: '24px',
                    }}
                    onChange={val => {
                        const numVal = val ? Number(val) : undefined;
                        handleRowChange(index, 'karat', numVal);
                    }}
                    options={finenessOptions.map(opt => ({
                        value: opt.toString(),
                        label: opt.toString(),
                    }))}
                    placeholder=""
                    allowClear
                    disabled={!rowsEditable}
                />
            ),
        },
        {
            title: columnNames.qty ?? t('metals.qty'),

            dataIndex: 'grams',
            render: (_: unknown, row: RowData, index: number) => (
                <InputNumber
                    value={row.grams}
                    style={{
                        width: 80,
                        fontSize: '11px',
                        height: '24px',
                    }}
                    onChange={val => handleRowChange(index, 'grams', val ?? undefined)}
                    disabled={!rowsEditable}
                />
            ),
        },
        {
            title: t('metals.pmp'),
            dataIndex: 'pmp',
            render: (_: unknown, row: RowData, index: number) => (
                <InputNumber
                    value={row.pmp}
                    style={{
                        width: 80,
                        fontSize: '11px',
                        height: '24px',
                    }}
                    onChange={val => handleRowChange(index, 'pmp', val ?? undefined)}
                    disabled={!rowsEditable}
                />
            ),
        },
        {
            title: columnNames.quantityToMelt ?? t('metals.quantityToMelt'),
            dataIndex: 'quantityToMelt',
            render: (_: unknown, row: RowData, index: number) => (
                <InputNumber
                    value={row.quantityToMelt}
                    style={{
                        width: 80,
                        fontSize: '11px',
                        height: '24px',
                    }}
                    min={0}
                    max={row.grams ?? 0}
                    onChange={val => handleRowChange(index, 'quantityToMelt', val ?? undefined)}
                    disabled={!rowsEditable}
                />
            ),
        },

        {
            title: '+/-',
            dataIndex: 'adjust',
            render: (_: unknown, row: RowData, index: number) => (
                <InputNumber
                    value={row.adjust}
                    style={{
                        width: 60,
                        fontSize: '11px',
                        height: '24px',
                    }}
                    onChange={val => handleRowChange(index, 'adjust', val ?? undefined)}
                    disabled={!rowsEditable}
                    // No onPressEnter: adjust is only used in summary, not to modify grams
                />
            ),
        },
        ...(showDeleteColumn
            ? [
                  {
                      title: '',
                      render: (_: unknown, __: RowData, index: number) => (
                          <Button
                              danger
                              size="small"
                              onClick={() => confirmDeleteRow(index)}
                              disabled={!rowsEditable || currentState.rows.length <= 1}
                          >
                              {t('metals.delete')}
                          </Button>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <div className="karat-calculator-compact">
            {modalContextHolder}
            {showControls && (
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder={t('metals.name')}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        style={{ width: 160, marginRight: 8 }}
                    />
                    <DatePicker value={date} onChange={setDate} style={{ marginRight: 8 }} />

                    <Button style={{ marginRight: 8 }}>{t('metals.print')}</Button>
                    <Button
                        style={{ marginRight: 8 }}
                        onClick={() =>
                            setMetalStates({
                                Gold: createDefaultState('Gold'),
                                Silver: createDefaultState('Silver'),
                                Platinum: createDefaultState('Platinum'),
                                Palladium: createDefaultState('Palladium'),
                            })
                        }
                    >
                        {t('metals.new')}
                    </Button>
                    <Button style={{ marginRight: 8 }}>{t('metals.save')}</Button>
                    <Button style={{ marginRight: 8 }}>{t('metals.load')}</Button>
                </div>
            )}
            {showControls && showLoadSelect && (
                <Select style={{ width: 200, marginBottom: 16 }} placeholder={t('common.select')} />
            )}

            <Tabs
                activeKey={activeMetal}
                onChange={key => setActiveMetal(key as MetalType)}
                items={metalTypeOptions.map(m => ({
                    key: m,
                    label: t(`metals.${m.toLowerCase()}`),
                }))}
            />

            <Table
                dataSource={currentState.rows}
                columns={columns}
                pagination={false}
                rowKey={(_, index) => String(index)}
                footer={() => <TableFooter onAddRow={addRow} t={t} rowsEditable={rowsEditable} />}
                size="small"
                style={{ fontSize: '11px' }}
                rowClassName={() => 'karat-calculator-row'}
            />
            <div
                style={{
                    marginTop: 16,
                    display: 'flex',
                    gap: 24,
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: 16,
                }}
            >
                {/* Column 1: Totals */}
                <div
                    style={{
                        flex: 1,
                        paddingRight: 20,
                        borderRight: '2px solid #1890ff',
                    }}
                >
                    <h4
                        style={{
                            marginBottom: 12,
                            color: '#1890ff',
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        {t('metals.totals')}
                    </h4>
                    <div style={{ marginBottom: 8 }}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: 4,
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            {t('metals.totalGrams')}:
                        </label>
                        <Input value={totalMass.toFixed(2)} readOnly style={{ width: '100%' }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: 4,
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            {t('metals.averagePurity')}:
                        </label>
                        <Input value={avgKarat.toFixed(2)} readOnly style={{ width: '100%' }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: 4,
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            {t('metals.pmp')}:
                        </label>
                        <Input value={avgPmp.toFixed(2)} readOnly style={{ width: '100%' }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: 4,
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            {t('metals.content999')}:
                        </label>
                        <Input value={total24.toFixed(2)} readOnly style={{ width: '100%' }} />
                    </div>
                    {meltingTotalMass > 0 && (
                        <div
                            style={{ marginTop: 16, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}
                        >
                            <h5 style={{ marginBottom: 8, color: '#1890ff', fontSize: '12px' }}>
                                {t('metals.meltingTotals')}:
                            </h5>
                            <div style={{ marginBottom: 4, fontSize: '11px' }}>
                                {t('metals.totalGrams')}: {meltingTotalMass.toFixed(2)}
                            </div>
                            <div style={{ marginBottom: 4, fontSize: '11px' }}>
                                {t('metals.content999')}: {meltingTotal24.toFixed(2)}
                            </div>
                            <div style={{ marginBottom: 4, fontSize: '11px' }}>
                                {t('metals.averagePurity')}: {meltingAvgKarat.toFixed(2)}
                            </div>
                            <div style={{ marginBottom: 4, fontSize: '11px' }}>
                                {t('metals.pmp')}: {meltingAvgPmp.toFixed(2)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Column 2: After Melting */}
                <div
                    style={{
                        flex: 1,
                        paddingLeft: 20,
                        paddingRight: 20,
                        borderRight: '2px solid #1890ff',
                    }}
                >
                    <h4
                        style={{
                            marginBottom: 12,
                            color: '#1890ff',
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        {t('metals.afterMelting')}
                    </h4>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 8 }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: 4,
                                    fontSize: '12px',
                                    color: '#666',
                                }}
                            >
                                {t('metals.afterMeltingGr')}:
                            </label>
                            <InputNumber
                                placeholder={t('metals.afterMeltingGr')}
                                value={currentState.afterMass}
                                onChange={val => {
                                    const num = typeof val === 'number' ? val : undefined;
                                    setCurrentState({
                                        ...currentState,
                                        afterMass: num,
                                    });
                                    onAfterValuesChange?.(num, currentState.afterKarat);
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: 4,
                                    fontSize: '12px',
                                    color: '#666',
                                }}
                            >
                                {t('metals.percentageAfterMelting')}:
                            </label>
                            <InputNumber
                                placeholder={t('metals.percentageAfterMelting')}
                                value={currentState.afterKarat}
                                onChange={val => {
                                    const num = typeof val === 'number' ? val : undefined;
                                    setCurrentState({
                                        ...currentState,
                                        afterKarat: num,
                                    });
                                    onAfterValuesChange?.(currentState.afterMass, num);
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    {Boolean(currentState.afterMass != null && currentState.afterKarat != null) && (
                        <div
                            style={{
                                backgroundColor: '#fff2e8',
                                border: '1px solid #ffcb85',
                                borderRadius: 4,
                                padding: 8,
                                fontSize: '12px',
                            }}
                        >
                            <p style={{ margin: '4px 0' }}>
                                {t('metals.afterMeltingContent')}:{' '}
                                <strong>{dupaTopitContinut.toFixed(2)}</strong>
                            </p>
                            <p style={{ margin: '4px 0' }}>
                                {t('metals.lossGr')}: <strong>{pierdereGr.toFixed(2)}</strong>
                            </p>
                            <p style={{ margin: '4px 0' }}>
                                {t('metals.lossPercent')}:{' '}
                                <strong>{pierdereProc.toFixed(2)}%</strong>
                            </p>
                            <p style={{ margin: '4px 0' }}>
                                {t('metals.totalLossPercent')}:{' '}
                                <strong>{pierdereProcTotal.toFixed(2)}%</strong>
                            </p>
                        </div>
                    )}
                </div>

                {/* Column 3: Karat Calculation */}
                <div
                    style={{
                        flex: 1,
                        paddingLeft: 20,
                    }}
                >
                    <h4
                        style={{
                            marginBottom: 12,
                            color: '#1890ff',
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        {t('metals.karatCalculation')}
                    </h4>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 8 }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: 4,
                                    fontSize: '12px',
                                    color: '#666',
                                }}
                            >
                                {t('metals.targetKarat')}:
                            </label>
                            <AutoComplete
                                placeholder={t('metals.targetKarat')}
                                style={{ width: '100%' }}
                                value={currentState.targetKarat?.toString() ?? ''}
                                onChange={val => {
                                    const numVal = val ? Number(val) : undefined;
                                    setCurrentState({ ...currentState, targetKarat: numVal });
                                }}
                                options={finenessOptions.map(opt => ({
                                    value: opt.toString(),
                                    label: opt.toString(),
                                }))}
                                allowClear
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: 4,
                                    fontSize: '12px',
                                    color: '#666',
                                }}
                            >
                                {t('metals.requiredKarat')}:
                            </label>
                            <AutoComplete
                                placeholder={t('metals.requiredKarat')}
                                style={{ width: '100%' }}
                                value={currentState.addKarat?.toString() ?? ''}
                                onChange={val => {
                                    const numVal = val ? Number(val) : undefined;
                                    setCurrentState({ ...currentState, addKarat: numVal });
                                }}
                                options={finenessOptions.map(opt => ({
                                    value: opt.toString(),
                                    label: opt.toString(),
                                }))}
                                allowClear
                            />
                        </div>
                    </div>
                    {Boolean(
                        currentState.targetKarat &&
                            currentState.addKarat &&
                            currentState.addKarat !== currentState.targetKarat,
                    ) && (
                        <div
                            style={{
                                backgroundColor: '#f6ffed',
                                border: '1px solid #b7eb8f',
                                borderRadius: 4,
                                padding: 8,
                                fontSize: '12px',
                            }}
                        >
                            <p style={{ margin: '4px 0' }}>
                                <strong>
                                    {gramsToAdd >= 0 ? t('metals.add') : t('metals.subtract')}
                                </strong>{' '}
                                {Math.abs(gramsToAdd).toFixed(2)} gr {currentState.addKarat}
                            </p>
                            <p style={{ margin: '4px 0' }}>
                                {t('metals.total')} {finalMass.toFixed(2)} gr {t('metals.gold')}{' '}
                                {currentState.targetKarat}
                            </p>
                            <p style={{ margin: '4px 0' }}>
                                {t('metals.equivalent')} {finalPure.toFixed(2)} gr{' '}
                                {t('metals.gold')} 999.9
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KaratCalculator;
